"use client";

import { motion } from "framer-motion";
import {
	AlertCircle,
	Clock,
	Leaf,
	Package,
	UtensilsCrossed,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useCart } from "@/components/cart/cart-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
	getMetafieldValue,
	parseMetafieldValue,
} from "@/lib/shopify/metafield-utils";
import type { Product } from "@/lib/shopify/types";
import { formatPrice } from "@/lib/shopify/utils";
import { cn } from "@/lib/utils";

interface ProductCardProps {
	product: Product;
	badge?: string;
	className?: string;
	priority?: boolean;
	onProductClick?: (product: Product) => void;
}

export function ProductCard({
	product,
	badge,
	className,
	priority = false,
	onProductClick,
}: ProductCardProps) {
	const { cart } = useCart();
	const router = useRouter();

	const deadlineMeta = product.metafields?.find(
		(m) => m?.namespace === "custom" && m?.key === "offer_deadline",
	)?.value;
	const [timeLeft, setTimeLeft] = useState<string | null>(null);

	useEffect(() => {
		if (!deadlineMeta) return;

		const calculateTimeLeft = () => {
			const target = new Date(deadlineMeta).getTime();
			const now = Date.now();
			const difference = target - now;

			if (difference > 0) {
				const days = Math.floor(difference / (1000 * 60 * 60 * 24));
				const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);

				if (days > 0) return `${days}d ${hours}h left`;

				const minutes = Math.floor((difference / 1000 / 60) % 60);
				return `${hours}h ${minutes}m left`;
			}
			return null;
		};

		setTimeLeft(calculateTimeLeft());
		const timer = setInterval(() => {
			setTimeLeft(calculateTimeLeft());
		}, 60000);

		return () => clearInterval(timer);
	}, [deadlineMeta]);

	const primaryImage = product.images.edges[0]?.node;
	const secondImage = product.images.edges[1]?.node;

	const MEAT_TAGS = [
		"Pork",
		"Turkey",
		"Chicken",
		"Elk",
		"Beef",
		"Bison",
		"Salmon",
		"Shrimp",
		"Fish",
		"Lamb",
		"Venison",
		"Duck",
	] as const;
	const TYPE_TAGS = ["Full Meal", "Meat Only"] as const;

	const meatSetLower = new Set(MEAT_TAGS.map((t) => t.toLowerCase()));
	const typeSetLower = new Set(TYPE_TAGS.map((t) => t.toLowerCase()));

	const dietaryVals = (product.tags ?? []).filter(
		(tag) =>
			!meatSetLower.has(tag.toLowerCase()) &&
			!typeSetLower.has(tag.toLowerCase()),
	);
	const meatVals = (product.tags ?? []).filter((tag) =>
		meatSetLower.has(tag.toLowerCase()),
	);
	const typeTagVals = (product.tags ?? []).filter((tag) =>
		typeSetLower.has(tag.toLowerCase()),
	);
	const formVals = parseMetafieldValue(
		getMetafieldValue(product, "custom", "food_product_form"),
	);
	const formAll = Array.from(new Set([...formVals, ...typeTagVals]));
	const productBadges = [
		...dietaryVals.map((v) => ({ label: v, type: "dietary" as const })),
		...formAll.map((v) => ({ label: v, type: "form" as const })),
		...meatVals.map((v) => ({ label: v, type: "meat" as const })),
	];

	const price = product.priceRange.minVariantPrice;
	const variant = product.variants[0];
	const isGiftCard =
		product.productType?.toLowerCase().includes("gift card") ||
		product.tags?.some((tag) => tag.toLowerCase() === "gift card") ||
		product.title.toLowerCase().includes("gift card") ||
		product.handle.toLowerCase().includes("gift-card");

	const isBurritoBox =
		product.handle === "ice-age-burritos" || product.handle === "burrito-box";

	const isBurritoProduct =
		isBurritoBox ||
		product.handle.includes("burrito") ||
		product.tags?.some((tag) => tag.toLowerCase().includes("burrito"));

	const isFbbBox =
		product.handle === "fbb-box" ||
		product.handle === "functional-bodybuilding-box";

	const productHref = isGiftCard
		? "/gift-cards"
		: isBurritoBox
			? "/burritos"
			: isFbbBox
				? "/fbb"
				: `/product/${product.handle}`;

	const isMultiVariantBox = isBurritoBox || isFbbBox;
	const isBaseAvailable = isGiftCard
		? true
		: isBurritoProduct
			? true
			: isMultiVariantBox && product.variants?.length
				? product.variants.some((v) => v.availableForSale)
				: Boolean(product.availableForSale) &&
					Boolean(variant ? variant.availableForSale !== false : true);

	const selectedVariantAvailableAfterCart = useMemo(() => {
		if (isGiftCard || isMultiVariantBox || isBurritoProduct) return null;
		if (
			!variant ||
			typeof variant.quantityAvailable !== "number" ||
			variant.quantityAvailable <= 0
		) {
			return null;
		}

		const inCartQuantity = (cart?.lines.edges || []).reduce((sum, edge) => {
			if (edge.node.merchandise.id !== variant.id) return sum;
			return sum + edge.node.quantity;
		}, 0);

		return Math.max(0, Math.trunc(variant.quantityAvailable) - inCartQuantity);
	}, [cart, isGiftCard, isMultiVariantBox, isBurritoProduct, variant]);

	const isAvailable = isGiftCard
		? true
		: isMultiVariantBox
			? isBaseAvailable
			: isBaseAvailable &&
				(selectedVariantAvailableAfterCart === null ||
					selectedVariantAvailableAfterCart > 0);

	const compareAtPrice = product.compareAtPriceRange?.minVariantPrice;
	const currentPriceAmount = parseFloat(price.amount);
	const compareAtPriceAmount = compareAtPrice
		? parseFloat(compareAtPrice.amount)
		: 0;
	const hasDiscount =
		compareAtPriceAmount > currentPriceAmount && compareAtPriceAmount > 0;
	const discountPercentage = hasDiscount
		? Math.round((1 - currentPriceAmount / compareAtPriceAmount) * 100)
		: 0;

	const handleAddToCart = async (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (isGiftCard) {
			router.push("/gift-cards");
			return;
		}

		if (isBurritoBox || isBurritoProduct) {
			router.push("/burritos");
			return;
		}

		if (isFbbBox) {
			router.push("/fbb");
			return;
		}

		// Individual meals ALWAYS route to Build a Box
		router.push(`/build-a-box?meal=${product.handle}`);
		return;
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.4 }}
			className={cn(
				"group flex flex-col h-full bg-card text-card-foreground rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300",
				!isAvailable && "opacity-75",
				className,
			)}
		>
			<Link
				href={productHref}
				className="relative block aspect-4/3 overflow-hidden bg-secondary"
				onClick={(e) => {
					if (onProductClick) {
						e.preventDefault();
						onProductClick(product);
					}
				}}
			>
				{primaryImage ? (
					<div className="absolute inset-0">
						<Image
							src={primaryImage.url || "/placeholder.svg"}
							alt={primaryImage.altText || product.title}
							fill
							priority={priority}
							className={cn(
								"object-cover transition-all duration-400 ease-out",
								secondImage && "group-hover:opacity-0",
							)}
							sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
						/>
						{secondImage && (
							<Image
								src={secondImage.url || "/placeholder.svg"}
								alt={secondImage.altText || `${product.title} - alternate view`}
								fill
								priority={priority}
								className="object-contain bg-white dark:bg-background transition-all duration-400 ease-out opacity-0 group-hover:opacity-100"
								sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
							/>
						)}
					</div>
				) : (
					<div className="flex h-full items-center justify-center text-muted-foreground">
						No image
					</div>
				)}

				<div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
					{badge && (
						<span className="inline-flex items-center gap-1 rounded-full bg-background/90 px-2.5 py-1 text-xs font-bold text-foreground backdrop-blur shadow-sm">
							<span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
							{badge}
						</span>
					)}

					{(hasDiscount || timeLeft) && (
						<>
							{timeLeft ? (
								<Badge className="bg-red-600 hover:bg-red-700 text-white font-bold shadow-sm">
									<Clock className="w-3 h-3 mr-1" />
									{timeLeft}
								</Badge>
							) : hasDiscount ? (
								<Badge className="bg-red-500 hover:bg-red-600 text-white font-bold">
									Limited Time Offer
								</Badge>
							) : null}
							{hasDiscount && (
								<Badge className="bg-red-500/90 hover:bg-red-600 text-white font-bold text-xs">
									-{discountPercentage}% OFF
								</Badge>
							)}
						</>
					)}

					{isAvailable &&
						selectedVariantAvailableAfterCart !== null &&
						selectedVariantAvailableAfterCart > 0 &&
						selectedVariantAvailableAfterCart <= 10 && (
							<Badge className="bg-amber-500 hover:bg-amber-600 text-white font-bold text-[10px] px-1.5 py-0.5 whitespace-nowrap shadow-sm">
								Only {selectedVariantAvailableAfterCart} left
							</Badge>
						)}
					{isAvailable &&
						selectedVariantAvailableAfterCart !== null &&
						selectedVariantAvailableAfterCart === 0 && (
							<Badge className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-[10px] px-1.5 py-0.5 whitespace-nowrap shadow-sm">
								Current available stock limit reached
							</Badge>
						)}
				</div>

				{!isAvailable && (
					<div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
						<Badge
							variant="secondary"
							className="text-sm font-bold px-4 py-2 bg-background/90"
						>
							<AlertCircle className="w-4 h-4 mr-1" />
							Out of Stock
						</Badge>
					</div>
				)}
			</Link>

			<div className="flex flex-1 flex-col p-4">
				<Link
					href={productHref}
					className="block"
					onClick={(e) => {
						if (onProductClick) {
							e.preventDefault();
							onProductClick(product);
						}
					}}
				>
					<h3 className="font-bold text-base sm:text-lg text-foreground line-clamp-2 md:line-clamp-1 group-hover:text-primary transition-colors">
						{product.title}
					</h3>
					{productBadges.length > 0 && (
						<div className="flex flex-wrap gap-1 mt-1.5">
							{productBadges.map((b) => (
								<span
									key={`${b.type}-${b.label}`}
									className={cn(
										"inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap",
										b.type === "dietary" &&
											"bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
										b.type === "form" &&
											"bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
										b.type === "meat" &&
											"bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
									)}
								>
									{b.type === "dietary" && (
										<Leaf className="w-2.5 h-2.5 shrink-0" aria-hidden="true" />
									)}
									{b.type === "form" && (
										<Package
											className="w-2.5 h-2.5 shrink-0"
											aria-hidden="true"
										/>
									)}
									{b.type === "meat" && (
										<UtensilsCrossed
											className="w-2.5 h-2.5 shrink-0"
											aria-hidden="true"
										/>
									)}
									{b.label}
								</span>
							))}
						</div>
					)}
					<p className="mt-1 sm:mt-2 text-xs sm:text-sm text-muted-foreground line-clamp-2 overflow-hidden text-ellipsis">
						{product.description ||
							"Detailed high-protein meal description available. Click to view nutritional info and ingredients."}
					</p>
				</Link>

				<div className="mt-auto pt-3 sm:pt-4 flex flex-col gap-3">
					<div className="flex flex-col gap-1.5">
						<div className="flex flex-wrap items-baseline gap-2">
							<span
								className={cn(
									"text-lg sm:text-xl font-bold",
									hasDiscount
										? "text-red-600 dark:text-red-500"
										: "text-foreground",
								)}
							>
								{isGiftCard
									? `From ${formatPrice(price.amount, price.currencyCode)}`
									: formatPrice(price.amount, price.currencyCode)}
							</span>
							{hasDiscount && compareAtPrice && (
								<span className="text-sm text-muted-foreground line-through decoration-muted-foreground/50">
									{formatPrice(
										compareAtPrice.amount,
										compareAtPrice.currencyCode,
									)}
								</span>
							)}
						</div>
						{hasDiscount && (
							<div className="flex items-center gap-2 text-xs font-bold text-red-600 dark:text-red-400">
								<span className="bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 rounded">
									Save{" "}
									{formatPrice(
										(compareAtPriceAmount - currentPriceAmount).toString(),
										price.currencyCode,
									)}
								</span>
								<span>({discountPercentage}% OFF)</span>
							</div>
						)}
						<span
							className={cn(
								"text-[10px] sm:text-xs font-medium",
								isAvailable
									? "text-emerald-600 dark:text-emerald-400"
									: "text-red-500",
							)}
						>
							{isAvailable
								? isGiftCard
									? "In Stock"
									: isBurritoBox
										? "In Stock (4 Box Varieties)"
										: isFbbBox
											? "In Stock (12 & 20 Pack)"
											: "In Stock"
								: "Out of Stock"}
						</span>
					</div>

					<Button
						onClick={handleAddToCart}
						className="w-full rounded-full font-bold shadow-sm hover:shadow-md transition-all active:scale-95"
						size="default"
						disabled={!isAvailable}
						aria-label={
							isGiftCard
								? "Buy Gift Card"
								: isBurritoBox || isBurritoProduct
									? "Buy via Burritos Box"
									: isFbbBox
										? "Buy via FBB Box"
										: `Build a box with ${product.title}`
						}
					>
						{!isAvailable
							? "Sold Out"
							: isGiftCard
								? "Buy Gift Card"
								: isBurritoBox || isBurritoProduct
									? "Buy via Burritos Box"
									: isFbbBox
										? "Buy via FBB Box"
										: "Buy via Build a Box"}
					</Button>
				</div>
			</div>
		</motion.div>
	);
}
